import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ls from 'localstorage-slim';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import DataContext from '../context/InvoiceData';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function Summary() {
  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const invoiceId = ls.get('id');
  const accessToken = ls.get('ETAToken');
  const subDomain = ls.get('subDomain');

  const {
    step,
    setStep,
    documentType,
    documentTypeVersion,
    setDocumentType,
    setDocumentTypeVersion,
    setInvoiceSubType,
  } = useContext(DataContext);
  const [summary, setSummary] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const onChange = nextStep => {
    setStep(nextStep < 0 ? 0 : nextStep > 5 ? 5 : nextStep);
  };

  const handleClose = () => {
    setStep(0);
    setDocumentTypeVersion('');
    setDocumentType('');
    setInvoiceSubType('');
    ls.remove('id');
    navigate('../../document/new');
  };

  const handleSubmit = () => {
    setIsLoading(true);
    const sendToEncryptionToast = toast.loading('Please wait...');
    axios
      .get(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/send_to_encryption/${invoiceId}`,
        {
          params: { accessToken },
        }
      )
      .then(response => {
        toast.update(sendToEncryptionToast, {
          render: `${response.data['message']}`,
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
        setIsLoading(false);
        ls.remove('id');
        setDocumentTypeVersion('');
        setDocumentType('');
        setStep(0);
        navigate('../documents/all');
      })
      .catch(error => {
        toast.update(sendToEncryptionToast, {
          render: `${error.message}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
        navigate('../document/draft');
      });
  };

  const handlePrevious = () => {
    onChange(step - 1);
  };

  useEffect(() => {
    async function fetchData() {
      const summaryToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/invoice_summary`,
          {
            params: { invoiceId },
          }
        );

        setDocumentTypeVersion(response.data.document_type_version);
        setDocumentType(
          response.data.document_type === 'i'
            ? 'Invoice'
            : response.data.document_type === 'c'
            ? 'Credit Note'
            : 'Debit Note'
        );
        setSummary(response.data);
        setIsLoading(false);

        toast.update(summaryToast, {
          render: 'Invoice summary retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(summaryToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(summaryToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(summaryToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <div style={{ height: '75vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div className={`${i18n.language === 'ar' && 'text-end'} card border-0 shadow m-3`}>
        <div className='card-body body mx-5 mt-5'>
          <span className='p-2 fw-bold fs-5'>{`${documentType} | ${documentTypeVersion}`}</span>
          <div
            className={`${
              i18n.language === 'en' ? 'row mx-0' : 'd-flex flex-row-reverse'
            } my-3 p-2`}>
            <span
              className='subtitle text-decoration-underline pb-2'
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('stepper.summary.title')}</strong>
            </span>
          </div>
          <div className={`${i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'} mx-2`}>
            <div className='col-7'>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-3'} col-6`}>
                  {t('stepper.summary.basicInfo.id')}
                </div>
                <div
                  className={`col text-nowrap ${
                    i18n.language === 'en' ? 'ms-1' : 'me-1'
                  } order-2 p-0`}>
                  {t('stepper.summary.basicInfo.date')}
                </div>
                <div className={`${i18n.language === 'en' ? 'order-3' : 'order-1'} col p-0`}></div>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-3'} col-6`}>
                  <div className='row bg-secondary text-dark bg-opacity-10 mx-0'>
                    <span
                      className={`border border-1 border-black ${
                        i18n.language === 'en' ? 'text-start' : 'text-end'
                      }`}>
                      {summary?.internal_id}
                    </span>
                  </div>
                </div>
                <div className='col order-2'>
                  <div className='row bg-secondary text-dark bg-opacity-10 ms-1'>
                    <span className='border border-1 border-black'>
                      {new Date(summary?.date_time_issued).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-3' : 'order-1'} col`}>
                  <div className='row bg-secondary text-dark bg-opacity-10 ms-1'>
                    <span className='border border-1 border-black'>
                      {new Date(summary?.date_time_issued).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='row mt-2 mx-2'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-7`}>
              <div className='row mx-0 text-white py-2' style={{ backgroundColor: '#04193d' }}>
                <span>{t('stepper.summary.issuerReceiver.issuer')}</span>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-9`}>
                  <div className='row mt-1'>
                    <span>{t('stepper.summary.issuerReceiver.name')}</span>
                  </div>
                  <div className='row mx-0 mt-1'>
                    <span className='border border-1 border-black'>
                      {summary?.issuer?.company_name_ar}
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-3`}>
                  <div className={`${i18n.language === 'en' ? 'text-start' : 'text-end'} row mt-1`}>
                    <span className='text-nowrap'>{t('stepper.summary.issuerReceiver.rin')}</span>
                  </div>
                  <div className='row mx-0 mt-1 ms-1'>
                    <span className='border border-1 border-black'>{summary.issuer?.rin}</span>
                  </div>
                </div>
              </div>
              <div className='row mt-1'>
                <span>{t('stepper.summary.issuerReceiver.type')}</span>
              </div>
              <div className='row mx-0 mt-1'>
                <span className='border border-1 border-black'>
                  {summary?.issuer?.type === 'B'
                    ? 'Business'
                    : summary?.issuer?.type === 'F'
                    ? 'Foreign'
                    : 'Personal'}
                </span>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-9`}>
                  <div className='row mt-1'>
                    <span>{t('stepper.summary.issuerReceiver.branch')}</span>
                  </div>
                  <div className='row mx-0 mt-1'>
                    <span className='border border-1 border-black'>
                      <div className='row '>
                        <span>{summary?.issuer?.building_number}</span>
                      </div>
                      <div className='row'>
                        <span>{summary?.issuer?.street}</span>
                      </div>
                      <div className='row '>
                        <span>{`${summary?.issuer?.region_city}, ${summary?.issuer?.governorate}, ${summary?.issuer?.country?.desc_en}`}</span>
                      </div>
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-3`}>
                  <div className='row mt-1'>
                    <span>{t('stepper.summary.issuerReceiver.code')}</span>
                  </div>
                  <div className='row mx-0 mt-1 ms-1'>
                    <span className='border border-1 border-black'>
                      {summary?.activity_code?.code}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-5`}>
              <div
                className={`${i18n.language === 'en' ? 'ms-1' : 'me-1'} row text-white py-2 `}
                style={{ backgroundColor: '#04193d' }}>
                <span>{t('stepper.summary.issuerReceiver.receiver')}</span>
              </div>
              <div className='row mx-0'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-7`}>
                  <div className='row mt-1'>
                    <span>{t('stepper.summary.issuerReceiver.name')}</span>
                  </div>
                  <div className={`row mx-0 mt-1`}>
                    <span className='border border-1 border-black'>
                      {summary?.receiver?.company_name_ar}
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-5`}>
                  <div className='row mx-0 mt-1'>
                    <span className='text-nowrap'>{t('stepper.summary.issuerReceiver.rin')}</span>
                  </div>
                  <div className={`row mx-0 mt-1`}>
                    <span className='border border-1 border-black'>{summary?.receiver?.rin}</span>
                  </div>
                </div>
              </div>
              <div className='row mt-1 mx-0'>
                <span>{t('stepper.summary.issuerReceiver.type')}</span>
              </div>
              <div className='row mt-1 mx-1'>
                <span className='border border-1 border-black'>
                  {summary?.receiver?.type === 'B'
                    ? 'Business'
                    : summary?.receiver?.type === 'F'
                    ? 'Foreign'
                    : 'Personal'}
                </span>
              </div>
              <div className='row mt-1 mx-0'>
                <span>{t('stepper.summary.issuerReceiver.address')}</span>
              </div>
              <div className='row mt-1 mx-1'>
                <span className='border border-1 border-black'>
                  <div className='row'>
                    <span>{summary?.receiver?.building_number}</span>
                  </div>
                  <div className='row'>
                    <span>{summary?.receiver?.street}</span>
                  </div>
                  <div className='row'>
                    <span>{`${summary?.receiver?.region_city}, ${summary?.receiver?.governorate}, ${summary?.receiver?.country?.desc_en}`}</span>
                  </div>
                </span>
              </div>
            </div>
          </div>
          <div className='row mt-2 mx-2'>
            <div
              className={`${i18n.language === 'en' ? 'row mx-0' : 'd-flex flex-row-reverse'} p-1`}>
              <span
                className='subtitle text-decoration-underline pb-2'
                style={{ textUnderlineOffset: '10px' }}>
                <strong className='fs-5'>{t('stepper.summary.lineItems.title')}</strong>
              </span>
            </div>
            <div className='row justify-content-center me-2'>
              <table className='table ms-4' dir={`${i18n.language === 'ar' ? 'rtl' : ''}`}>
                <thead
                  style={{
                    verticalAlign: 'middle',
                  }}>
                  <tr className='text-center'>
                    <th scope='col'>{t('stepper.summary.lineItems.table.codeName')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.itemCode')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.description')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.quantity')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.unitPrice')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary?.invoiceLines?.map(invoiceLine => (
                    <tr className='text-center' key={invoiceLine?.id}>
                      <td>{invoiceLine?.internalCode}</td>
                      <td>{invoiceLine?.itemCode}</td>
                      <td>{invoiceLine?.description}</td>
                      <td>
                        {invoiceLine?.quantity}
                        {' - '}
                        {invoiceLine?.unitType}
                      </td>
                      <td>{invoiceLine?.unitValue?.amountEGP}</td>
                      <td>{invoiceLine?.salesTotal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div
                className={`${
                  i18n.language === 'en'
                    ? 'ms-4 row'
                    : 'd-flex flex-row-reverse justify-content-end'
                } shadow`}>
                <div className={`${i18n.language === 'en' ? 'offset-8' : ''} col-4`}>
                  <div className='row'>
                    <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col`}>
                      <span className='text-nowrap d-block my-1'>
                        {t('stepper.summary.lineItems.totals.sales')}
                      </span>
                      <span className='text-nowrap d-block mb-1'>
                        {t('stepper.summary.lineItems.totals.discount')}
                      </span>
                      <span className='text-nowrap d-block mb-1'>
                        {t('stepper.summary.lineItems.totals.itemsDiscount')}
                      </span>
                      <span className='text-nowrap d-block mb-1'>
                        {t('stepper.summary.lineItems.totals.vat')}
                      </span>
                      <span className='text-nowrap d-block mb-2'>
                        {t('stepper.summary.lineItems.totals.extra')}
                      </span>
                      <span className='text-nowrap d-block fw-bold fs-6 mb-2'>
                        {t('stepper.summary.lineItems.totals.total')}
                      </span>
                    </div>
                    <div
                      className={`${
                        i18n.language === 'en' ? 'text-start order-2' : 'text-end order-1'
                      } col`}>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block my-1`}>
                        {summary?.total_sales_amount}
                      </span>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block my-1`}>
                        {summary?.total_discount_amount}
                      </span>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block my-1`}>
                        {summary?.total_items_discount_amount}
                      </span>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block my-1`}>
                        {summary?.taxTotals}
                      </span>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block my-1`}>
                        {summary?.extra_discount_amount}
                      </span>
                      <span
                        className={`${
                          i18n.language === 'en' ? 'text-end' : 'text-start'
                        } d-block fw-bold fs-6 mb-2`}>
                        {summary?.total_amount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
        } justify-content-evenly fixed-bottom py-3 bg-secondary bg-gradient bg-opacity-10`}>
        <button
          className={`${
            i18n.language === 'ar' && 'offset-6'
          } button text-center col-1 bg-white text-dark border border-1 border-dark`}
          onClick={handleClose}>
          {t('stepper.footer.close')}
        </button>
        <button
          className={`${i18n.language === 'en' && 'offset-6'} button text-center col-1 ${
            step === 0 ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handlePrevious}
          disabled={step === 0}>
          {t('stepper.footer.previous')}
        </button>
        <button className='button text-center col-1' onClick={handleSubmit}>
          {t('stepper.footer.submit')}
        </button>
      </div>
    </>
  );
}

export default Summary;
